import { useEffect, useState } from 'react'
import { Play } from 'lucide-react'
import { mediaURL } from '../api'
import type { Asset } from '../types'
import { mediaDuration } from '../lib/utils'
import { Button, Dialog } from './ui'

export function ProductDemoPreview({ ws, asset }: { ws: string; asset?: Asset }) {
  if (!asset) return null
  return <DemoPreview key={`${ws}/${asset.storage_key}`} ws={ws} asset={asset} />
}

function DemoPreview({ ws, asset }: { ws: string; asset: Asset }) {
  const [open, setOpen] = useState(false)
  const [url, setURL] = useState('')
  const [posterURL, setPosterURL] = useState('')
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [seconds, setSeconds] = useState(asset.duration_s)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setURL('')
    setPosterURL('')
    setFailed(false)
    mediaURL(ws, asset.storage_key)
      .then(value => { if (!cancelled) setURL(value) })
      .catch(() => { if (!cancelled) setFailed(true) })
    if (asset.poster_key) {
      mediaURL(ws, asset.poster_key)
        .then(value => { if (!cancelled) setPosterURL(value) })
        // The video can still play if its optional poster is unavailable.
        .catch(() => {})
    }
    return () => { cancelled = true }
  }, [open, ws, asset.storage_key, asset.poster_key, attempt])

  return (
    <>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-ink-dim">Duration: {mediaDuration(seconds)}</span>
        <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>
          <Play /> Preview demo
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen} title={`Preview: ${asset.name}`}>
        <p className="mb-3 text-sm text-ink-dim">Duration: {mediaDuration(seconds)}</p>
        {failed ? (
          <div className="space-y-3" role="alert">
            <p className="text-sm text-bad">Could not play this demo. Try loading it again.</p>
            <Button type="button" onClick={() => setAttempt(value => value + 1)}>Retry preview</Button>
          </div>
        ) : url ? (
          <video
            src={url} poster={posterURL || undefined} controls playsInline preload="metadata"
            aria-label={`Product demo: ${asset.name}`}
            className="max-h-[65vh] w-full rounded-[--radius-control] bg-black object-contain"
            onLoadedMetadata={event => {
              const length = event.currentTarget.duration
              if (Number.isFinite(length) && length >= 0) setSeconds(length)
            }}
            onError={() => setFailed(true)}
          />
        ) : <p role="status" className="text-sm text-ink-dim">Loading preview…</p>}
      </Dialog>
    </>
  )
}
